import { useEffect, useState } from 'react';
import {
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Box, Card, Chip, Link, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import {
  HotWalletItem,
  ProviderItem,
  listHotWallets,
  listProviders,
} from '../../../services/xbitRelaySweepService';
import {
  DEFAULT_SWEEP_PROVIDER,
  XBIT_RELAY_SWEEP_HISTORY_PERMISSION_RESOURCE,
} from './constants';
import { chainSymbol, explorerTxUrl, statusColor, truncate } from './helpers';
import './init';

const SweepHistoryListActions = ({
  providers,
  chains: chainChoices,
}: {
  providers: ProviderItem[];
  chains: HotWalletItem[];
}) => (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      px: 3,
      pt: 1,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <TextInput
            key="userId"
            source="userId"
            label="module.xbitRelaySweepHistory.userId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 200 }}
          />,
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelaySweepHistory.provider"
            choices={providers.map((p) => ({ id: p.provider, name: p.provider }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 200 }}
          />,
          <SelectInput
            key="chainId"
            source="chainId"
            label="module.xbitRelaySweepHistory.chain"
            choices={chainChoices.map((c) => ({
              id: c.chainId,
              name: `${c.chainName} (${c.chainId})`,
            }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="address"
            source="address"
            label="module.xbitRelaySweepHistory.address"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
        ]}
      />
    </Box>
  </TopToolbar>
);

const TxLink = ({ chainId, txHash }: { chainId: number; txHash: string }) => {
  if (!txHash) return <>-</>;
  const url = explorerTxUrl(chainId, txHash);
  if (!url) {
    return (
      <Tooltip title={txHash}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {truncate(txHash)}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={txHash}>
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontFamily: 'monospace' }}
      >
        {truncate(txHash)}
      </Link>
    </Tooltip>
  );
};

const XbitRelaySweepHistoryList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [providers, setProviders] = useState<ProviderItem[]>([]);
  const [chainChoices, setChainChoices] = useState<HotWalletItem[]>([]);

  useEffect(() => {
    listProviders()
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    listHotWallets(DEFAULT_SWEEP_PROVIDER)
      .then((res) => setChainChoices(res.data?.wallets ?? []))
      .catch(() => setChainChoices([]));
  }, []);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied to clipboard', { type: 'info' });
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_SWEEP_HISTORY_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={t('module.xbitRelaySweepHistory.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelaySweepHistory.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={
              <SweepHistoryListActions providers={providers} chains={chainChoices} />
            }
            empty={false}
            perPage={20}
            exporter={false}
            pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
            sort={{ field: 'collectTimeUnixMs', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
                sx={{
                  '& .RaDatagrid-headerCell': { fontWeight: 600 },
                  '& .RaDatagrid-rowCell': { py: 1.25 },
                }}
              >
                <FunctionField
                  label="module.xbitRelaySweepHistory.time"
                  sortable={false}
                  render={(record: any) =>
                    record.collectTimeUnixMs ? (
                      <Tooltip
                        title={dayjs(Number(record.collectTimeUnixMs)).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                        >
                          {dayjs(Number(record.collectTimeUnixMs)).format('MM-DD HH:mm')}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.chain"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={String(record.chainId)}>
                      <Chip
                        label={chainSymbol(record.chainId)}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.token"
                  sortable={false}
                  render={(record: any) => {
                    const symbol = record.symbol;
                    if (symbol) {
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {symbol}
                        </Typography>
                      );
                    }
                    if (!record.token) return <>-</>;
                    return (
                      <Tooltip title={record.token}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                          onClick={() => copy(record.token)}
                        >
                          {truncate(record.token)}
                        </Typography>
                      </Tooltip>
                    );
                  }}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.amount"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.amount || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.status"
                  sortable={false}
                  render={(record: any) => (
                    <Chip
                      label={record.status || '-'}
                      size="small"
                      color={statusColor(record.status) as any}
                      variant={record.status === 'SKIPPED' ? 'outlined' : 'filled'}
                    />
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.txHash"
                  sortable={false}
                  render={(record: any) => (
                    <TxLink chainId={record.chainId} txHash={record.txHash} />
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.preConditionTxid"
                  sortable={false}
                  render={(record: any) => (
                    <TxLink chainId={record.chainId} txHash={record.preConditionTxid} />
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.postConditionTxid"
                  sortable={false}
                  render={(record: any) => (
                    <TxLink chainId={record.chainId} txHash={record.postConditionTxid} />
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.address"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.address || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.address)}
                      >
                        {truncate(record.address)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.userId"
                  sortable={false}
                  render={(record: any) =>
                    record.userId ? (
                      <Tooltip title={record.userId}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => copy(record.userId)}
                        >
                          {truncate(record.userId)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelaySweepHistory.errorMsg"
                  sortable={false}
                  render={(record: any) => {
                    if (!record.errorMsg) return '-';
                    return (
                      <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-line' }}>
                        {record.errorMsg}
                      </Typography>
                    );
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XbitRelaySweepHistoryList;
